import { addMinutes, addHours, getMinutes, setSeconds, setMilliseconds } from 'date-fns'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'
import moment from 'moment-timezone'

import { getAssetLink } from '../../../../helpers/get-asset-link'
import { transformMimeType } from '../../../../helpers/fileValidation'

export const getBuildingOptionsPmOrFd = (isPm, isFd, userData) => {
  if (isPm) {
    return userData.buildingIds.map((building) => ({
      ...building,
      value: building.id,
      label: building.name,
    }))
  }

  if (isFd) {
    return [
      {
        value: userData.buildingId.id,
        label: userData.buildingId.name,
        id: userData.buildingId.id,
        timezone: userData.buildingId.timezone,
        isHoa: userData.buildingId.isHoa,
      },
    ]
  }
}

export const getBuildingOptions = (buildings) =>
  buildings.map((building) => ({
    ...building,
    value: building.id,
    label: building.name,
  }))

export const getEventTypeOptions = (trainingTypes = []) =>
  trainingTypes
    .filter((type) => !type.personal)
    .map((classType) => ({
      id: classType.id,
      avatar: classType.icon,
      text: classType.name,
      color: classType.color,
      personal: classType.personal,
      bookingType: classType.bookingType ? classType.bookingType.name : '',
      value: classType.id,
      label: classType.name,
    }))

export const getTrainerOptions = (trainers) =>
  trainers.map((trainer) => ({
    id: trainer.id,
    avatar: getAssetLink({ path: 'chitown-avatar/avatar', file: trainer.user.avatar }) || 'clothes',
    text: trainer.user.firstName + ' ' + trainer.user.lastName,
    value: trainer.id,
    label: trainer.user.firstName + ' ' + trainer.user.lastName,
  }))

// t defaults to an identity fallback so non-UI callers (mappers/form-state defaults) that only
// ever read `.value` don't need to thread a translation function through.
/**
 * @param {Date | string | number} [date]
 * @param {any} [t]
 */
export const getFrequencyOptions = (date, t = (key) => key) => {
  const dateMoment = moment(date)
  const dayOfWeek = dateMoment.format('dddd') // e.g., "Monday"
  const dayOfWeekWithDate = dateMoment.format('Do') // e.g., "15th"

  const weekOccurrence = Math.ceil(dateMoment.date() / 7) // Determine which week (1st, 2nd, etc.)
  const ordinalMap = [
    t('eventManagement:common.frequency.first'),
    t('eventManagement:common.frequency.second'),
    t('eventManagement:common.frequency.third'),
    t('eventManagement:common.frequency.fourth'),
  ]
  const ordinal = ordinalMap[weekOccurrence - 1] || t('eventManagement:common.frequency.fourth') // Fallback to fourth if out of range
  return [
    { value: 'none', label: t('eventManagement:common.frequency.doesNotRepeat') },
    { value: 'daily', label: t('eventManagement:common.frequency.daily') },
    { value: 'weekdays', label: t('eventManagement:common.frequency.weekdaysOnly') },
    { value: 'weekly', label: t('eventManagement:common.frequency.weeklyOn', { dayOfWeek }) },
    {
      value: 'monthly',
      label: t('eventManagement:common.frequency.monthlyOn', { dayOfWeekWithDate }),
    },
    {
      value: 'monthly-on-day',
      label: t('eventManagement:common.frequency.monthlyOnThe', { ordinal, dayOfWeek }),
    },
  ]
}

// The default is always the 'none' option's value, regardless of locale/labels.
export const defaultFrequencyOption = 'none'

export const mergeDateAndTime = (datePart, timePart) => {
  const newDate = new Date(datePart)

  newDate.setHours(
    timePart.getHours(),
    timePart.getMinutes(),
    timePart.getSeconds(),
    timePart.getMilliseconds(),
  )

  return newDate
}

export const getTimeOptions = (t) => [
  {
    value: '1 hour',
    label: t('eventManagement:common.hoursBeforeEvent', { count: 1 }),
  },
  {
    value: '3 hours',
    label: t('eventManagement:common.hoursBeforeEvent', { count: 3 }),
  },
  {
    value: '6 hours',
    label: t('eventManagement:common.hoursBeforeEvent', { count: 6 }),
  },
  {
    value: '12 hours',
    label: t('eventManagement:common.hoursBeforeEvent', { count: 12 }),
  },
  {
    value: '1 day',
    label: t('eventManagement:common.daysBeforeEvent', { count: 1 }),
  },
  {
    value: '2 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 2 }),
  },
  {
    value: '3 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 3 }),
  },
  {
    value: '4 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 4 }),
  },
  {
    value: '5 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 5 }),
  },
  {
    value: '6 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 6 }),
  },
  {
    value: '7 days',
    label: t('eventManagement:common.daysBeforeEvent', { count: 7 }),
  },
]

export const getReminderTimeArray = (remiderTimes) => {
  const array = remiderTimes.filter(Boolean).map((time) => {
    const [amount, timeUnit] = time.split(' ')

    if (timeUnit === 'hours') {
      return parseInt(amount)
    }
    if (timeUnit === 'hour') {
      return 1
    }
    return amount * 24
  })
  return array.length ? array : null
}

export const getDuration = (timeStart, timeEnd) => {
  const timeEndDate = new Date(timeEnd)
  const timeStartDate = new Date(timeStart)
  const diffMs = timeEndDate - timeStartDate
  const diffMinutes = diffMs / (1000 * 60)

  return Math.abs(diffMinutes)
}

export const fetchFileFromRemote = (url) => {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => blob)
}

export const getRemindTimeFromEventData = (timeArray) => {
  if (timeArray.length === 0) {
    return ['']
  }

  return timeArray.map((time) => {
    if (time > 24) {
      return `${time / 24} days`
    } else if (time === 24) {
      return '1 day'
    } else if (time === 1) {
      return `${time} hour`
    } else {
      return `${time} hours`
    }
  })
}

export const getInitialTimeStart = (timezone) => {
  const now = new Date()
  const zonedDate = toZonedTime(now, timezone)
  const oneHourLater = addHours(zonedDate, 1)

  const currentMinutes = getMinutes(oneHourLater)
  const minutesToNextQuarter = 15 - (currentMinutes % 15)

  const roundedStart = addMinutes(oneHourLater, minutesToNextQuarter)

  const cleanStart = setMilliseconds(setSeconds(roundedStart, 0), 0)

  return cleanStart
}

export const getInitialTimeEnd = (timezone) => {
  const start = getInitialTimeStart(timezone)
  return addMinutes(start, 15)
}

export const calculateDate = (originalDate, timezone) => {
  const year = originalDate.getFullYear()
  const month = originalDate.getMonth() + 1
  const day = originalDate.getDate()
  const hour = originalDate.getHours()
  const minute = originalDate.getMinutes()
  const second = originalDate.getSeconds()

  const localStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0',
  )} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(
    2,
    '0',
  )}`

  const utcDate = fromZonedTime(localStr, timezone)
  return utcDate.getTime()
}

export const maxNumberOfReminder = 3

export const FILE_MAX_SIZE = 3000000 // 3MB
export const AVAILABLE_FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png']
export const MAX_FILES_NUMBER = 5

export const validateAdditionalFile = (
  file,
  numberOfFiles,
  maxFileSize,
  accept,
  maxNumberOfFiles,
  t,
) => {
  let error = ''

  if (file.size > maxFileSize) {
    error = t('eventManagement:common.fileMaxSize', { size: maxFileSize / 1000000 })
  }

  const fileType = transformMimeType(file.type)

  if (!accept.includes(fileType)) {
    error = t('eventManagement:common.fileTypeNotSupported', { types: accept.join(', ') })
  }

  if (numberOfFiles >= maxNumberOfFiles) {
    error = t('eventManagement:common.maxFilesAllowed', { max: maxNumberOfFiles })
  }

  return error
}

export const SOCIAL_LINKS_PAGE = '/#/dashboard/building-dashboard/social-links'

export const isDateNotChanged = (orginalDate, formDate) => {
  const originalStartTime = new Date(orginalDate).getTime()
  const formStartTime = new Date(formDate).getTime()
  const startTimeNotChanged = originalStartTime === formStartTime

  return startTimeNotChanged
}

export const visibilityDefaultValue = false

export const getVisibilityOptions = (t) => [
  {
    text: t('eventManagement:common.visibilityPublic'),
    value: false,
    tooltips: [t('eventManagement:common.visibilityPublicTooltip')],
  },
  {
    text: t('eventManagement:common.visibilityPrivate'),
    value: true,
    tooltips: [t('eventManagement:common.visibilityPrivateTooltip')],
  },
]
